/* lp-lazy-video-play.js
 *
 * Lazy-play controller for product-card and PDP-gallery <video> elements
 * marked with `data-lp-lazy-play`.
 *
 * Why a custom controller and not native autoplay alone:
 *   1. Many cards on a long page = many decoded video frames = jank + heat
 *      on mobile. IntersectionObserver lets us only play what's visible.
 *   2. iOS Safari historically refuses to play more than ~6 inline videos
 *      concurrently; the rest silently freeze on their poster. We enforce
 *      a hard cap and pause the videos farthest from the viewport center
 *      whenever the cap is exceeded.
 *   3. prefers-reduced-motion should NEVER autoplay decorative video. We
 *      strip `autoplay` and keep the poster visible for those users.
 *   4. El markup NO lleva `autoplay` y sale con `preload="none"` a proposito.
 *      Con `autoplay` en el HTML el navegador baja el mp4 entero apenas
 *      parsea el tag, aunque el video este a tres pantallas de distancia y
 *      aunque despues lo pausemos: el home bajaba 2 x 550KB de un video que
 *      nadie habia visto todavia. Sin autoplay + preload="none" no se pide
 *      un solo byte hasta que este controlador llama play(), y el warm-up
 *      de abajo se encarga de que igual llegue bufereado a tiempo.
 *
 * The controller is idempotent: it tags each <video> with a sentinel
 * (`__lpLazyPlayInit`) so re-running the IIFE (after AJAX section render,
 * theme editor reload, etc.) does not double-observe or double-bind.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var SELECTOR = 'video[data-lp-lazy-play]';
  var MAX_CONCURRENT = 6; // iOS Safari practical limit for inline <video>
  var ENTER_THRESHOLD = 0.5;

  // Respect the OS-level reduced-motion preference. If the user opted in,
  // we never autoplay; the poster stays visible and the <video> behaves
  // like a still image. We still strip the `autoplay` attribute so the
  // browser does not start playback before our JS gets a chance to run.
  var reduceMotionMql = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var prefersReducedMotion = !!(reduceMotionMql && reduceMotionMql.matches);

  if (!('IntersectionObserver' in window)) {
    // Old browsers: don't try to manage playback. If autoplay works it
    // works; if not, the poster shows. Either outcome is acceptable.
    return;
  }

  // Track which videos the IO currently considers "in view". We use this
  // set to enforce MAX_CONCURRENT — when it overflows we pause the videos
  // whose viewport-center distance is largest.
  var inViewVideos = new Set();

  function distanceFromViewportCenter(video) {
    var rect = video.getBoundingClientRect();
    var videoCenterY = rect.top + rect.height / 2;
    var viewportCenterY = window.innerHeight / 2;
    return Math.abs(videoCenterY - viewportCenterY);
  }

  function safePlay(video) {
    // Marca para el warm-up: a partir de aca el elemento es "suyo" y nadie
    // mas puede llamar load() encima.
    video.__lpStarted = true;
    // .play() returns a promise that rejects on autoplay-policy violations
    // (most common: user has not interacted yet on a desktop browser that
    // requires it for unmuted audio). Our videos are all muted, so the
    // promise should resolve in modern browsers — but we still swallow the
    // rejection so it doesn't surface as an uncaught error.
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        // El primer play() puede fallar si el video todavía no tiene datos
        // decodificables (con preload="none" arranca sin un solo byte, y el
        // warm-up puede no haber llegado a bufferear). Reintentamos
        // una sola vez cuando el navegador avisa que ya puede reproducir. Si
        // el rechazo fue por política de autoplay, el canplay igual reintenta
        // (muted, así que en navegadores modernos resuelve).
        if (video.__lpPlayRetry) return;
        video.__lpPlayRetry = true;
        var onReady = function () {
          video.removeEventListener('canplay', onReady);
          var p2 = video.play();
          if (p2 && typeof p2.catch === 'function') p2.catch(function () { /* queda en poster */ });
        };
        video.addEventListener('canplay', onReady);
      });
    }
  }

  function enforceConcurrencyCap() {
    if (inViewVideos.size <= MAX_CONCURRENT) return;

    // Sort by distance from viewport center, ascending. Closest stays
    // playing, farthest get paused (but kept in inViewVideos so when
    // they get closer again they resume without re-firing the observer).
    var sorted = Array.from(inViewVideos).sort(function (a, b) {
      return distanceFromViewportCenter(a) - distanceFromViewportCenter(b);
    });

    for (var i = 0; i < sorted.length; i++) {
      if (i < MAX_CONCURRENT) {
        if (sorted[i].paused) safePlay(sorted[i]);
      } else {
        if (!sorted[i].paused) sorted[i].pause();
      }
    }
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= ENTER_THRESHOLD) {
        inViewVideos.add(video);
        if (!prefersReducedMotion) safePlay(video);
      } else {
        inViewVideos.delete(video);
        if (!video.paused) video.pause();
      }
    });
    enforceConcurrencyCap();
  }, {
    // Varios thresholds en vez de uno solo. Con [0.5] a secas, si el video
    // entra en vista de un scroll (o si la PDP recién lo muestra), el único
    // evento al cruzar 0.5 puede reportar un ratio marginalmente menor y
    // perderse: el video queda pausado en el poster hasta que se lo saca de
    // vista y vuelve a entrar (o, en la PDP, hasta hacer click en otra foto y
    // volver). Con pasos intermedios, algún evento con ratio >= ENTER_THRESHOLD
    // siempre dispara el play.
    threshold: [0, 0.25, 0.5, 0.75, 1]
  });

  // Warm-up: con preload="none" el navegador no tiene NI el header del mp4,
  // asi que un play() en frio muestra el poster un instante antes del primer
  // frame. Este segundo observer, con un margen de una pantalla, adelanta el
  // buffering justo antes de que el video entre en vista: para cuando cruza
  // el 50% que exige el observer de arriba, ya suele estar listo para pintar.
  // El costo sigue acotado a lo que el visitante esta por ver — un video tres
  // pantallas abajo nunca se toca. Una sola vez por video: despues de disparar
  // dejamos de observarlo.
  var warmIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var video = entry.target;
      warmIo.unobserve(video);
      if (prefersReducedMotion) return;
      // Si el video ya arranco no lo tocamos: load() reinicia el elemento y
      // cortaria la reproduccion en curso. Puede pasar de verdad — un video
      // visible al cargar la pagina cruza los dos observers a la vez y el
      // orden entre callbacks de observers distintos no esta garantizado.
      if (video.__lpStarted) return;
      if (video.preload !== 'none') return;
      video.preload = 'auto';
      try { video.load(); } catch (_) {}
    });
  }, { rootMargin: '100% 0px' });

  // When the page scrolls, the distance-from-center ordering changes, so
  // re-evaluate which N=6 should be playing. Throttled via rAF so we never
  // do more than one recalc per frame even on a fast-scrolling iOS device.
  var scrollScheduled = false;
  function onScroll() {
    if (scrollScheduled) return;
    scrollScheduled = true;
    window.requestAnimationFrame(function () {
      scrollScheduled = false;
      enforceConcurrencyCap();
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  function observeVideo(video) {
    if (video.__lpLazyPlayInit) return;
    video.__lpLazyPlayInit = true;

    // If the browser ignored our liquid-level decisions and the video is
    // playing already, pause it until the IO confirms it's actually visible.
    if (prefersReducedMotion) {
      video.removeAttribute('autoplay');
      try { video.pause(); } catch (_) {}
    }

    io.observe(video);
    warmIo.observe(video);
  }

  function scanAndObserve(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < videos.length; i++) observeVideo(videos[i]);
  }

  // Initial pass. Wrapped in DOMContentLoaded gate so we don't miss videos
  // appended later in the document by deferred scripts that run before this
  // one (the layout loads us defer last, but section partials may still
  // come in via Shopify section rendering API on AJAX swap).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanAndObserve(document); });
  } else {
    scanAndObserve(document);
  }

  // Watch for dynamically-added videos (AJAX search results, predictive
  // search, cart drawer recommendations, theme-editor section reload, etc.).
  // We narrow the work to subtrees that *might* contain a matching video
  // before doing the querySelectorAll.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) {
            observeVideo(node);
          } else if (node.querySelectorAll) {
            scanAndObserve(node);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // React to a runtime change in the reduced-motion preference (rare but
  // possible: user toggles the OS setting with the page open). Modern
  // browsers expose `addEventListener('change', ...)`; older Safari uses
  // the deprecated `addListener` — guard for both.
  if (reduceMotionMql) {
    var onPrefChange = function (e) {
      prefersReducedMotion = !!e.matches;
      if (prefersReducedMotion) {
        // Pause everything we currently consider in-view.
        inViewVideos.forEach(function (v) { if (!v.paused) v.pause(); });
      } else {
        // Resume the ones the cap allows.
        enforceConcurrencyCap();
      }
    };
    if (typeof reduceMotionMql.addEventListener === 'function') {
      reduceMotionMql.addEventListener('change', onPrefChange);
    } else if (typeof reduceMotionMql.addListener === 'function') {
      reduceMotionMql.addListener(onPrefChange);
    }
  }
})();
